import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ConfirmDelete } from "@/components/training/ConfirmDelete";
import { toast } from "sonner";
import { Pencil, Trash2, CalendarDays, ListChecks } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { getTrainingNotes, updateTrainingNote, deleteTrainingNote } from "@/lib/dentiumApi";
import { rangeLabel, errText } from "@/components/training/trainingUtils";

const STATUS = {
  activo: { label: "Activo ahora", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  programado: { label: "Programado", cls: "bg-sky-50 text-sky-700 border-sky-200" },
  pausado: { label: "Pausado", cls: "bg-[#F4F2EE] text-[#8A7E70] border-[#E7E2DA]" },
  caducado: { label: "Caducado", cls: "bg-[#F4F2EE] text-[#A79E90] border-[#E7E2DA]" },
};

const NoteRow = ({ n, onEdit }) => {
  const qc = useQueryClient();
  const [confirm, setConfirm] = useState(false);
  const refresh = () => qc.invalidateQueries({ queryKey: ["training-notes"] });
  const toggle = useMutation({
    mutationFn: (active) => updateTrainingNote(n.id, { text: n.text, starts_on: n.starts_on, ends_on: n.ends_on, active }),
    onSuccess: refresh, onError: (e) => toast.error(errText(e, "No se pudo cambiar")),
  });
  const remove = useMutation({
    mutationFn: () => deleteTrainingNote(n.id),
    onSuccess: () => { refresh(); toast.success("Aviso eliminado", { description: "El asistente ya no lo tendrá en cuenta." }); },
    onError: (e) => toast.error(errText(e, "No se pudo eliminar")),
  });
  const st = STATUS[n.status];
  return (
    <div data-testid={`training-note-${n.id}`} className={`px-4 py-3.5 flex gap-3 items-start ${n.status === "caducado" ? "opacity-60" : ""}`}>
      <div className="min-w-0 flex-1">
        <p className="text-sm text-[#1A1A1A] whitespace-pre-wrap">{n.text}</p>
        <div className="flex flex-wrap items-center gap-2 mt-1.5">
          <span data-testid="training-note-status" className={`text-[11px] font-medium rounded-full border px-2 py-0.5 ${st.cls}`}>{st.label}</span>
          <span className="text-[11px] text-[#8A7E70] inline-flex items-center gap-1"><CalendarDays className="h-3 w-3" /> {rangeLabel(n)}</span>
          {n.created_by && <span className="text-[11px] text-[#A79E90]">· {n.created_by}</span>}
        </div>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        {n.status !== "caducado" && (
          <Switch data-testid="training-note-toggle" checked={n.active} disabled={toggle.isPending} onCheckedChange={(v) => toggle.mutate(v)} title={n.active ? "Pausar" : "Reactivar"} />
        )}
        <button data-testid="training-note-edit" onClick={() => onEdit(n)} title="Editar" className="h-8 w-8 rounded-full flex items-center justify-center text-[#6B6157] hover:bg-[#F1EBE1]"><Pencil className="h-4 w-4" /></button>
        <ConfirmDelete open={confirm} onOpenChange={setConfirm} testid="training-note-delete-dialog" title="¿Eliminar este aviso?"
          description="El asistente dejará de tenerlo en cuenta. Si solo quieres pararlo un tiempo, usa el interruptor para pausarlo." onConfirm={() => remove.mutate()} />
        <button data-testid="training-note-delete" onClick={() => setConfirm(true)} title="Eliminar" className="h-8 w-8 rounded-full flex items-center justify-center text-[#A79E90] hover:bg-red-50 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
      </div>
    </div>
  );
};

export const NotesList = ({ onEdit }) => {
  const { data } = useQuery({ queryKey: ["training-notes"], queryFn: getTrainingNotes, refetchInterval: 60000 });
  const items = data?.items || [];
  const active = items.filter((i) => i.status === "activo").length;
  return (
    <div className="rounded-2xl border border-[#E7E2DA] bg-white overflow-hidden" data-testid="training-notes-list">
      <div className="px-5 py-3.5 border-b border-[#EDE7DE] flex items-center gap-2">
        <ListChecks className="h-4 w-4 text-[#1A1A1A]" />
        <h2 className="text-sm font-semibold text-[#1A1A1A]">Avisos que conoce el asistente</h2>
        <span data-testid="training-notes-count" className="ml-auto text-xs text-[#8A7E70]">{active} activos · {items.length} en total</span>
      </div>
      {items.length === 0 ? (
        <p data-testid="training-notes-empty" className="px-5 py-10 text-center text-sm text-[#8A7E70]">Aún no le has enseñado nada. Prueba con un cierre o una promoción.</p>
      ) : (
        <div className="divide-y divide-[#EDE7DE]">{items.map((n) => <NoteRow key={n.id} n={n} onEdit={onEdit} />)}</div>
      )}
    </div>
  );
};
